import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Pause,
  PlayArrow,
  Person,
  Assignment,
  TrendingUp,
  Group,
  CheckCircle,
  Refresh,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { leadsService } from '../../services/leads';
import LoadingSpinner from '../common/LoadingSpinner';

const LeadFlowControlDashboard = () => {
  const [assignmentStats, setAssignmentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pauseReason, setPauseReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [refreshing, setRefreshing] = useState(false);

  const { user } = useSelector(state => state.auth);

  useEffect(() => {
    fetchAssignmentStats();
  }, []);

  const fetchAssignmentStats = async () => {
    try {
      setLoading(true);
      const data = await leadsService.getAssignmentStats();
      setAssignmentStats(data);
    } catch (error) {
      console.error('Failed to fetch assignment stats:', error);
      showSnackbar('Failed to fetch assignment statistics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAssignmentStats();
    setRefreshing(false);
    showSnackbar('Data refreshed successfully', 'success');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const canControlUser = (targetUser) => {
    if (!user || !targetUser) return false;

    // Admin can control anyone
    if (user.role === 'admin') return true;

    // Manager can control themselves and their team leads
    if (user.role === 'manager') {
      return targetUser.id === user.id || targetUser.role === 'team_lead';
    }

    // Team lead can control their BDAs but NOT themselves
    if (user.role === 'team_lead') {
      return targetUser.role === 'bda' && targetUser.id !== user.id;
    }

    return false;
  };

  const handlePauseFlow = (targetUser) => {
    setSelectedUser(targetUser);
    setPauseReason('');
    setPauseDialogOpen(true);
  };

  const handleResumeFlow = (targetUser) => {
    setSelectedUser(targetUser);
    setResumeDialogOpen(true);
  };

  const confirmPauseFlow = async () => {
    if (!selectedUser || !pauseReason.trim()) {
      showSnackbar('Please provide a reason for pausing lead flow', 'error');
      return;
    }

    try {
      await leadsService.pauseLeadFlow(selectedUser.id, pauseReason.trim());
      showSnackbar(`Lead flow paused for ${selectedUser.username}`, 'success');
      setPauseDialogOpen(false);
      setPauseReason('');
      await fetchAssignmentStats();
    } catch (error) {
      console.error('Failed to pause lead flow:', error);
      showSnackbar(error.response?.data?.error || 'Failed to pause lead flow', 'error');
    }
  };

  const confirmResumeFlow = async () => {
    if (!selectedUser) return;

    try {
      await leadsService.resumeLeadFlow(selectedUser.id);
      showSnackbar(`Lead flow resumed for ${selectedUser.username}`, 'success');
      setResumeDialogOpen(false);
      await fetchAssignmentStats();
    } catch (error) {
      console.error('Failed to resume lead flow:', error);
      showSnackbar(error.response?.data?.error || 'Failed to resume lead flow', 'error');
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'error',
      manager: 'warning',
      team_lead: 'info',
      bda: 'success'
    };
    return colors[role] || 'default';
  };

  const formatLastAssigned = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) return 'Less than 1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} days ago`;
  };

  if (loading) {
    return <LoadingSpinner message="Loading lead flow dashboard..." />;
  }

  if (!assignmentStats) {
    return (
      <Alert severity="error">
        Failed to load assignment statistics. Please try refreshing the page.
      </Alert>
    );
  }

  const { assignment_stats, summary } = assignmentStats;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Lead Flow Control Dashboard</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Group color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{summary.total_users}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PlayArrow color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{summary.active_flow_users}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Flow Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Assignment color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{summary.total_assignments}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Assignments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{summary.total_active_leads}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Leads
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Assignment Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            User Assignment Details
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Assignment Areas</TableCell>
                  <TableCell>Lead Stats</TableCell>
                  <TableCell>Last Assignment</TableCell>
                  <TableCell>Flow Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignment_stats.map((userStats) => (
                  <TableRow key={userStats.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {userStats.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {userStats.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={userStats.role}
                        color={getRoleColor(userStats.role)}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Box>
                        {userStats.assigned_states.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              States:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {userStats.assigned_states.map((state, index) => (
                                <Chip key={index} label={state} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {userStats.assigned_diseases.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Diseases:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {userStats.assigned_diseases.map((disease, index) => (
                                <Chip key={index} label={disease} size="small" variant="outlined" color="secondary" />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          Total: {userStats.total_leads_assigned}
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          Active: {userStats.active_leads_count}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={userStats.last_lead_assigned_at ? new Date(userStats.last_lead_assigned_at).toLocaleString() : 'Never'}>
                        <Typography variant="body2">
                          {formatLastAssigned(userStats.last_lead_assigned_at)}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {userStats.lead_flow_enabled ? (
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                            icon={<CheckCircle />}
                          />
                        ) : (
                          <Chip
                            label="Paused"
                            color="error"
                            size="small"
                            icon={<Pause />}
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {canControlUser(userStats) && (
                          <>
                            {userStats.lead_flow_enabled ? (
                              <Tooltip title="Pause lead flow">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handlePauseFlow(userStats)}
                                >
                                  <Pause />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Resume lead flow">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleResumeFlow(userStats)}
                                >
                                  <PlayArrow />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                        {!canControlUser(userStats) && (
                          <Chip label="No Access" size="small" variant="outlined" disabled />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pause Lead Flow Dialog */}
      <Dialog open={pauseDialogOpen} onClose={() => setPauseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Pause Lead Flow
          {selectedUser && (
            <Typography variant="caption" display="block" color="text.secondary">
              User: {selectedUser.username} ({selectedUser.role})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for Pause *"
            multiline
            rows={3}
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
            placeholder="e.g., User on vacation for 2 weeks"
            sx={{ mt: 2 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            This will stop automatic lead assignments to this user. They can still work on their existing leads.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmPauseFlow}
            startIcon={<Pause />}
          >
            Pause Lead Flow
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Lead Flow Dialog */}
      <Dialog open={resumeDialogOpen} onClose={() => setResumeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Resume Lead Flow
          {selectedUser && (
            <Typography variant="caption" display="block" color="text.secondary">
              User: {selectedUser.username} ({selectedUser.role})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Alert severity="success">
            This will resume automatic lead assignments to this user based on their assigned states and diseases.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResumeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={confirmResumeFlow}
            startIcon={<PlayArrow />}
          >
            Resume Lead Flow
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadFlowControlDashboard;